import { cn } from "@lib/cn";
import type { ReactNode } from "react";
import type { DocumentInfoPill, DocumentInfoSize } from "../types";

// To add a pill: extend `DocumentInfoPill`, append here, and render it in `DocumentInfo.library`.
export const AVAILABLE_PILLS: { key: DocumentInfoPill; label: string }[] = [
  { key: "sentBy", label: "Sent by" },
  { key: "readingTime", label: "Reading time" },
];

/** Default order — enabled pills render in this order regardless of toggle order. */
export const PILL_ORDER: DocumentInfoPill[] = AVAILABLE_PILLS.map((p) => p.key);

// Per Figma 206:21161 — the pill shell is size-constant; only text, avatar, and icon scale.
const SIZE: Record<
  DocumentInfoSize,
  { text: string; avatar: number; icon: number }
> = {
  small: { text: "tw-text-xs", avatar: 20, icon: 16 },
  medium: { text: "tw-text-sm", avatar: 24, icon: 18 },
  large: { text: "tw-text-base", avatar: 28, icon: 20 },
};

export const Pill = ({
  size,
  children,
}: {
  size: DocumentInfoSize;
  children: ReactNode;
}) => (
  <span
    className={cn(
      "tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-overflow-hidden tw-rounded-full tw-border tw-border-black/5 tw-bg-white tw-p-2 tw-text-blue-base tw-shadow-xs",
      SIZE[size].text,
    )}
  >
    {children}
  </span>
);

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0]![0] + (parts[1]?.[0] ?? "")).toUpperCase();
};

export const SentByPill = ({
  size,
  name,
  picture,
}: {
  size: DocumentInfoSize;
  name: string;
  picture?: string | null;
}) => {
  const avatar = SIZE[size].avatar;
  return (
    <Pill size={size}>
      {picture ? (
        <img
          src={picture}
          alt=""
          // Images are natively draggable: without this, pressing the avatar
          // and moving starts the browser's own image drag and dnd-kit never
          // sees the gesture, so the element can't be reordered from here.
          draggable={false}
          className="tw-shrink-0 tw-rounded-full tw-object-cover"
          style={{ width: avatar, height: avatar }}
        />
      ) : (
        <span
          className={cn(
            "tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-blue-100 tw-bg-blue-100/25 tw-font-semibold tw-uppercase tw-text-blue-base",
            SIZE[size].text,
          )}
          style={{ width: avatar, height: avatar }}
        >
          {getInitials(name)}
        </span>
      )}
      <span className="tw-whitespace-nowrap tw-px-1">Sent by {name}</span>
    </Pill>
  );
};

const ClockIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="tw-shrink-0"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);

export const ReadingTimePill = ({
  size,
  time,
}: {
  size: DocumentInfoSize;
  time: string;
}) => {
  const box = SIZE[size].avatar;
  return (
    <Pill size={size}>
      <span
        className="tw-flex tw-shrink-0 tw-items-center tw-justify-center"
        style={{ width: box, height: box }}
      >
        <ClockIcon size={SIZE[size].icon} />
      </span>
      <span className="tw-whitespace-nowrap tw-px-1">Reading time: {time}</span>
    </Pill>
  );
};
